import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import notificationSound from "../../../static/audios/notification-sound.mp3";
import halfLogo from "../../../static/images/logo/half.svg";
import markLogo from "../../../static/images/logo/mark.svg";
import wongwianYaiLogo from "../../../static/images/logo/wongwian-yai.svg";
import { clearAuthTokens } from "../api/client";
import { logout as logoutSession } from "../api/services/clinic/account";
import Button from '../components/Button';
import Icon from '../components/Icon';
import MenuList from "../components/MenuList";
import { useApp } from "../contexts/AppContext";
import { useUser } from "../contexts/UserContext";
import SummarySettingsModal from "../pages/visting_management/summary/SummarySettingsModal";
import realtimeClient from "../realtime/client";
import handleClickOutsideElement from '../utils/handleClickOutsideElement';
import { canAccessPage } from "../utils/roleAccess";

const INITIAL_NOTIFICATION_COUNTS = {
  queue_management: 0,
  consultation_room: 0,
  medications_and_supplies: 0,
  payment: 0,
};

const NAVIGATION_EXPANDED_WIDTH_CLASS = "w-[256px]";
const NAVIGATION_COLLAPSED_WIDTH_CLASS = "w-20";
const NAVIGATION_POPOVER_WIDTH_CLASS = "w-[220px]";
const NAVIGATION_POPOVER_VIEWPORT_MARGIN = 8;

const MENU_ITEMS = [
  // {
  //   page: "dashboard",
  //   label: "แดชบอร์ด",
  //   url: "/dashboard",
  //   icon: "template",
  //   disabled: true,
  // },
  {
    page: "patient_management",
    label: "จัดการผู้ป่วย",
    url: "/patient-management",
    icon: "user-group",
  },
  {
    page: "queue_management",
    label: "จัดการคิว",
    url: "/queue-management",
    icon: "clipboard-list",
  },
  {
    page: "consultation_room",
    label: "ห้องตรวจ",
    url: "/visiting-management",
    icon: "doctor",
  },
  {
    page: "medications_and_supplies",
    label: "ยาและเวชภัณฑ์",
    url: "/pharmacy-dispense-management",
    icon: "pill",
  },
  {
    page: "payment",
    label: "การชำระเงิน",
    url: "/payments",
    icon: "cash"
  },
  {
    page: "medical_certificate_template",
    label: "แม่แบบใบรับรองแพทย์",
    url: "/medical-certificate-template",
    icon: "shopping-bag",
  },
  {
    page: "appointment",
    label: "ระบบนัดหมาย",
    url: "/appointment-management",
    icon: "calendar",
  },
  {
    page: "follow_up",
    label: "ระบบติดตามอาการ",
    url: "/follow-up-management",
    icon: "device-tablet",
  },
  {
    page: "lab_results_list",
    label: "เอกสารผลแล็บ",
    url: "/lab-results-list",
    icon: "beaker",
  },
  {
    page: "clinic_stock",
    label: "สินค้าคงคลัง",
    url: "/inventory",
    icon: "table",
  },
  {
    page: "settings",
    label: "ระบบคลินิก",
    url: "/settings",
    icon: "cog",
  },
];

/**
 * Navigation component
 *
 * Renders the main sidebar navigation for the application, including
 * user information, navigation menu items, and an expandable/collapsible
 * layout. The component adjusts its appearance based on expansion state
 * and highlights the currently active menu.
 *
 * @param {object} props
 *
 * @param {string} [props.currentMenu="dashboard"]
 *   The key of the currently active navigation menu item. Used to apply
 *   selected/active styling in the sidebar.
 *
 * @param {boolean} [props.isExpanded=true]
 *   Whether the sidebar is expanded (showing text and full layout) or
 *   collapsed (icons only).
 */
export default function Navigation({
  currentMenu = "dashboard",
  isExpanded = true,
}) {
  const [expanded, setExpanded] = useState(isExpanded);
  const [active, setActive] = useState(currentMenu);
  const [navigationPopoverOpen, setNavigationPopoverOpen] = useState(false);
  const [navigationPopoverPlacement, setNavigationPopoverPlacement] = useState("top");
  const [isProcessingLogout, setIsProcessingLogout] = useState(false);
  const [isSummarySettingsModalOpen, setIsSummarySettingsModalOpen] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState(INITIAL_NOTIFICATION_COUNTS);
  const popupRef = useRef(null);
  const audioRef = useRef(null);
  const hasReceivedNotificationCountsRef = useRef(false);
  const { appVersion } = useApp();
  const { user, setUser } = useUser();
  const navigate = useNavigate();
  const clinicBranchText = user?.current_clinic_branch
    ? `${user.current_clinic_branch.display_name} (${user.current_clinic_branch.code})`
    : "";
  const versionText = `v${appVersion?.version ?? ""} ${appVersion?.stage ?? ""} ${appVersion?.release_date ?? ""}`.trim();
  const navigationPopoverTriggerIcon = expanded ? "chevron-down" : "dots-vertical";
  const canManageSummarySettings = user?.role === "แพทย์";

  useEffect(() => {
    setActive(currentMenu);
  }, [currentMenu]);

  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.preload = "auto";

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  handleClickOutsideElement(popupRef, () => setNavigationPopoverOpen(false));

  const playNotificationSound = useCallback(async () => {
    if (!audioRef.current) {
      return false;
    }

    audioRef.current.currentTime = 0;
    try {
      await audioRef.current.play();
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleNotificationCountUpdated = useCallback((payload = {}) => {
    const data = payload?.data?.data ?? payload?.data ?? {};
    setNotificationCounts((prev) => {
      const next = {
        queue_management: data.queue_management ?? prev.queue_management ?? 0,
        consultation_room: data.consultation_room ?? prev.consultation_room ?? 0,
        medications_and_supplies: data.medications_and_supplies ?? prev.medications_and_supplies ?? 0,
        payment: data.payment ?? prev.payment ?? 0,
      };
      const isInitialPayload = !hasReceivedNotificationCountsRef.current;
      const visibleNotificationPages = MENU_ITEMS
        .filter((item) => Object.hasOwn(next, item.page) && canAccessPage(user, item.page))
        .map((item) => item.page);
      const hasIncrease = visibleNotificationPages.some((page) => next[page] > (prev[page] ?? 0));

      if (!isInitialPayload && hasIncrease) {
        void playNotificationSound();
      }

      hasReceivedNotificationCountsRef.current = true;
      return next;
    });
  }, [playNotificationSound, user]);

  useEffect(() => {
    const unsubscribe = realtimeClient.subscribe("notification_count_updated", handleNotificationCountUpdated);
    return () => {
      unsubscribe();
    };
  }, [handleNotificationCountUpdated]);

  useLayoutEffect(() => {
    if (!navigationPopoverOpen) {
      setNavigationPopoverPlacement("top");
      return undefined;
    }

    function syncNavigationPopoverPlacement() {
      const popupElement = popupRef.current;

      if (!popupElement) {
        return;
      }

      const rect = popupElement.getBoundingClientRect();
      const isOverflowingTop = rect.top < NAVIGATION_POPOVER_VIEWPORT_MARGIN;
      const isOverflowingLeft = rect.left < NAVIGATION_POPOVER_VIEWPORT_MARGIN;
      const isOverflowingRight = rect.right > window.innerWidth - NAVIGATION_POPOVER_VIEWPORT_MARGIN;
      const isOverflowingBottom = rect.bottom > window.innerHeight - NAVIGATION_POPOVER_VIEWPORT_MARGIN;

      setNavigationPopoverPlacement((currentPlacement) => {
        let nextPlacement = currentPlacement;

        if (currentPlacement === "top") {
          nextPlacement = (isOverflowingTop || isOverflowingLeft || isOverflowingRight || isOverflowingBottom)
            ? "right-top"
            : "top";
        } else if (isOverflowingBottom) {
          nextPlacement = "right-bottom";
        } else if (isOverflowingTop) {
          nextPlacement = "right-top";
        }

        return currentPlacement === nextPlacement ? currentPlacement : nextPlacement;
      });
    }

    syncNavigationPopoverPlacement();
    window.addEventListener("resize", syncNavigationPopoverPlacement);

    return () => {
      window.removeEventListener("resize", syncNavigationPopoverPlacement);
    };
  }, [navigationPopoverOpen, navigationPopoverPlacement]);

  function getExpandedLogoSrc() {
    const branchDisplayName = user?.current_clinic_branch?.code;
    switch (branchDisplayName) {
      case "B001":
        return wongwianYaiLogo;
      default:
        return halfLogo;
    }
  }

  function buildLogo() {
    const expandedLogoSrc = getExpandedLogoSrc();
    return (
      <div onClick={() => navigate("/")}>
        <div
          className={`sidebar-logo flex items-center justify-center p-3 ${expanded ? "" : "hidden"}`}
        >
          <img src={expandedLogoSrc} alt="Clinic logo" width="161" height="48" />
        </div>
        <div
          className={`sidebar-logo items-center justify-center py-3 ${expanded ? "hidden" : "flex"}`}
        >
          <img src={markLogo} alt="Mark logo" width="39" height="48" />
        </div>
      </div>
    );
  }

  function buildToggleButton() {
    return (
      <div className="absolute top-[96px] right-[-16px]">
        <Button hierarchy="secondary" size="sm" icon={expanded ? "arrow-sm-left" : "arrow-sm-right"} onClick={() => setExpanded(!expanded)} />
      </div>
    );
  }

  function onClickMenu(item) {
    if (item.disabled) return;
    setActive(item.page);
    navigate(item.url);
  }

  async function handleLogout() {
    setNavigationPopoverOpen(false);
    try {
      await logoutSession();
    } catch (error) {
      console.error("ไม่สามารถออกจากระบบได้", error);
    }
    clearAuthTokens();
    setUser(null);
    navigate("/account/login", { replace: true });
  }

  function checkIfShouldShowNavigationItem(item) {
    return canAccessPage(user, item.page);
  }

  function toggleNavigationPopover() {
    setNavigationPopoverOpen((prev) => !prev);
  }

  function handleOpenSummarySettingsModal() {
    setNavigationPopoverOpen(false);
    setIsSummarySettingsModalOpen(true);
  }

  async function handleClickLogoutMenu() {
    setIsProcessingLogout(true);
    try {
      await handleLogout();
    } finally {
      setIsProcessingLogout(false);
    }
  }

  function buildNavigation() {
    return (
      <nav className="flex-1 flex-col">
        {MENU_ITEMS.map((item) => {
          if (!checkIfShouldShowNavigationItem(item)) {
            return null;
          }

          const count = notificationCounts[item.page] ?? 0;
          const showBadge = count > 0;

          return (
            <ul key={item.page} className={`nav-container pb-3 ${expanded ? "" : "flex items-center justify-center"}`}>
              <button
                className={`nav-item ${expanded ? "w-full" : "w-8"} flex items-center gap-2 ${active === item.page ? "data-[selected=true]" : ""
                  }`}
                data-selected={active === item.page}
                onClick={() => onClickMenu(item)}
                disabled={item.disabled}
              >
                <span className="flex items-center gap-2 flex-1">
                  <span className="relative flex items-center">
                    <Icon name={item.icon} size="md" />
                    {!expanded && showBadge && (
                      <span className="absolute -top-[3px] -right-[3px] h-3 w-3 rounded-full bg-creation-error-solid-surface-default" />
                    )}
                  </span>
                  {expanded && <p className="sidebar-text px-2">{item.label}</p>}
                </span>
                {expanded && showBadge && (
                  <span className="ml-auto inline-flex min-w-[32px] min-h-[24px] items-center justify-center rounded-lg bg-vivid-tangerine-100 text-vivid-tangerine-700 text-caption font-bold">
                    {count}
                  </span>
                )}
              </button>
            </ul>
          );
        })}
      </nav>
    );
  }

  function buildFooter() {
    return (
      <div className="gap-5 flex flex-col">
        <div className="gap-3 flex flex-row justify-between">
          <div
            className={`sidebar-text gap-3 flex flex-col ${expanded ? "flex" : "hidden"}`}
          >
            <p className="font-ui font-header text-subtitle text-main-typography-system-default">
              {user?.full_name || ""}
            </p>
            <p className="font-ui font-body text-body-base text-main-typography-system-description">
              {user?.role || ""}
            </p>
            {clinicBranchText ? (
              <p className="font-ui font-body text-body-small text-main-typography-system-description">
                {clinicBranchText}
              </p>
            ) : null}
          </div>
          {buildNavigationPopoverTrigger()}
        </div>
        {versionText ? (
          <p className={`sidebar-text font-ui font-body text-caption text-main-typography-system-caption ${expanded ? "text-center" : "hidden"}`}>
            {versionText}
          </p>
        ) : null}
      </div>
    );
  }

  function buildNavigationPopoverMenu() {
    const popoverPlacementClassName = navigationPopoverPlacement === "right-top"
      ? "left-full top-0 ml-2"
      : navigationPopoverPlacement === "right-bottom"
        ? "left-full bottom-0 ml-2"
        : "right-0 bottom-full mb-2";

    return (
      <div
        ref={popupRef}
        data-testid="navigation-popover-menu"
        className={`absolute ${popoverPlacementClassName} flex ${NAVIGATION_POPOVER_WIDTH_CLASS} flex-col gap-1 rounded-md bg-creation-neutral-container-surface-default p-3 shadow-md z-50`}
      >
        {canManageSummarySettings ? (
          <MenuList
            id="navigation-summary-settings"
            label="ตั้งค่าสรุปบันทึก"
            icon="adjustments"
            onClick={handleOpenSummarySettingsModal}
          />
        ) : null}
        <MenuList
          id="navigation-logout"
          label="ออกจากระบบ"
          icon="logout"
          disabled={isProcessingLogout}
          onClick={handleClickLogoutMenu}
        />
      </div>
    );
  }

  function buildNavigationPopoverTrigger() {
    const trigger = (
      <div className="relative">
        <Button
          hierarchy="tertiary"
          size="md"
          icon={navigationPopoverTriggerIcon}
          onClick={toggleNavigationPopover}
        />
        {navigationPopoverOpen && buildNavigationPopoverMenu()}
      </div>
    );

    if (expanded) {
      return (
        <div className="sidebar-button w-8 h-8 rounded-md p-3 flex items-center justify-center">
          <div className="ml-auto flex-shrink-0">
            {trigger}
          </div>
        </div>
      );
    }

    return (
      <div className="sidebar-button w-full flex-col items-center justify-center gap-3">
        <div className="w-8 h-8 rounded-md p-3 flex items-center justify-center">
          {trigger}
        </div>
      </div>
    );
  }

  return (
    <aside
      id="sidebar"
      className={
        `
        relative h-screen bg-main-background-system-default 
        border-r border-main-border-system-default transition-all duration-300
        flex flex-col gap-6 py-6 flex-none z-50
        ` + (expanded ? ` ${NAVIGATION_EXPANDED_WIDTH_CLASS} px-[20px]` : ` ${NAVIGATION_COLLAPSED_WIDTH_CLASS} px-[16px]`)
      }
    >
      {buildLogo()}
      <hr className="border-main-border-system-default" />
      {buildToggleButton()}
      {buildNavigation()}
      <hr className="border-main-border-system-default" />
      {buildFooter()}
      <SummarySettingsModal
        isOpen={isSummarySettingsModalOpen}
        onClose={() => setIsSummarySettingsModalOpen(false)}
      />
    </aside>
  );
}
